import type { ProgressStageKey } from "@-/dev.improve.haniverse.lib.learning-stats.entities";
import { useCSSVariable } from "@-/com.npmjs.uniwind";

/**
 * The four mastery stages, ordered along the warm light-yellow → dark-orange
 * ramp (lightest for `new`, darkest for `mastered`). The actual colors live as
 * Uniwind theme tokens in `apps/haniverse-expo/global.css` (`--color-stage-*`),
 * which back the `text-stage-*` / `bg-stage-*` / `border-stage-*` utilities —
 * className consumers (e.g. the progress rows) should use those classes
 * directly rather than importing hex values.
 *
 * This module exists only for consumers that need the raw hex (chart libraries
 * take color *props*, not classNames): {@link useStageColor} resolves a stage's
 * theme token to a hex string at runtime so charts stay in sync with the tokens
 * and flip with the color scheme.
 */
export const PROGRESS_STAGE_KEYS = [
  "new",
  "familiar",
  "proficient",
  "mastered",
] as const satisfies readonly ProgressStageKey[];

export const PROGRESS_STAGE_BACKGROUND: Record<ProgressStageKey, string> = {
  familiar: "bg-stage-familiar",
  mastered: "bg-stage-mastered",
  new: "bg-stage-new",
  proficient: "bg-stage-proficient",
};

/**
 * Resolve a mastery stage's `--color-stage-*` theme token to its current hex
 * value (reactive to light/dark mode). Use for chart color *props*; for
 * `className`-driven UI use the `text-stage-*` / `bg-stage-*` / `border-stage-*`
 * utilities instead.
 */
function stageColorFromVariable(value: string | number | undefined): string {
  return value === undefined || value === "" ? "#000000" : String(value);
}

export function useStageColor(stage: ProgressStageKey): string {
  // Uniwind statically analyzes `useCSSVariable` call sites — dynamic names like
  // `--color-stage-${stage}` are not registered and warn at runtime.
  const newColor = useCSSVariable("--color-stage-new");
  const familiarColor = useCSSVariable("--color-stage-familiar");
  const proficientColor = useCSSVariable("--color-stage-proficient");
  const masteredColor = useCSSVariable("--color-stage-mastered");

  const byStage: Record<ProgressStageKey, string | number | undefined> = {
    familiar: familiarColor,
    mastered: masteredColor,
    new: newColor,
    proficient: proficientColor,
  };

  return stageColorFromVariable(byStage[stage]);
}
